import React, {useState, useEffect} from 'react';
import Cookies from 'js-cookie';
import { Radar } from 'react-chartjs-2';
import logoIcon from "../../assets/logo-icon.png";

const translations = {
  en: {
    mbtiWorkStyle: "MBTI Work Style",
    mbtiAnalysis: "MBTI Analysis",
    showTeamImpact: "Show Team Impact",
    candidatesMbtiType: "Candidate's MBTI Type",
    yourMbtiType: "Your MBTI Type",
    workplaceApproach: "Workplace Approach",
    workStyle: "Work Style",
    teamDynamics: "Team Dynamics",
    communicationStyle: "Communication Style",
    keyStrengths: "Key Strengths",
    growthAreas: "Growth Areas",
    mbtiDimensions: "MBTI Dimensions",
    preference: "Preference",
    teamImpact: "Team Impact",
    careerAlignment: "Career Alignment",
    idealRoles: "Ideal Roles",
    workEnvironmentFit: "Work Environment Fit",
    developmentFocus: "Development Focus"
  },
  nl: {
    mbtiWorkStyle: "MBTI Werkstijl",
    mbtiAnalysis: "MBTI Analyse",
    showTeamImpact: "Toon Team Impact",
    candidatesMbtiType: "MBTI Type Kandidaat",
    yourMbtiType: "Jouw MBTI Type",
    workplaceApproach: "Werkplek Aanpak",
    workStyle: "Werkstijl",
    teamDynamics: "Team Dynamiek",
    communicationStyle: "Communicatiestijl",
    keyStrengths: "Belangrijkste Sterke Punten",
    growthAreas: "Ontwikkelingsgebieden",
    mbtiDimensions: "MBTI Dimensies",
    preference: "Voorkeur",
    teamImpact: "Team Impact",
    careerAlignment: "Carrière Afstemming",
    idealRoles: "Ideale Rollen",
    workEnvironmentFit: "Werkomgeving Passendheid",
    developmentFocus: "Ontwikkelingsfocus"
  }
};

const MBTISection = ({ mbtiData, analysisResults, colors, isRecruiterMode, isGeneratingPDF, isDutch = false }) => {
    const [lang, setLang] = useState(isDutch ? 'nl' : 'en');
    const t = translations[lang];
    const [isMbtiToggleOpen, setIsMbtiToggleOpen] = useState(false)

    const mbtiOptions = {
        scales: {
            r: {
                angleLines: {
                    display: true
                },
                suggestedMin: 0,
                suggestedMax: 6,
                ticks: {
                    stepSize: 20
                }
            }
        },
        plugins: {
            legend: {
                display: false
            }
        }
    };

    const toggleOpen = () => {
        setIsMbtiToggleOpen(!isMbtiToggleOpen);
        Cookies.set('isMbtiToggleOpen', !isMbtiToggleOpen)
      };

    useEffect(() => {
        const checkLanguage = () => {
            const cookieIsDutch = Cookies.get('isDutch') === 'true';
            const newLang = cookieIsDutch ? 'nl' : 'en';
            if (newLang !== lang) {
                setLang(newLang);
            }
        };
        checkLanguage();
        const interval = setInterval(checkLanguage, 1000);
        return () => clearInterval(interval);
    }, [lang]);

    useEffect(() => {
        setIsMbtiToggleOpen(true)
    }, [isRecruiterMode])

    return (
        <div className="flex flex-col animate-text">
            <div className='flex items-center justify-between gap-2'>
            <h2 className="text-xl font-bold text-primary">
                {isRecruiterMode ? t.mbtiWorkStyle : t.mbtiAnalysis}
            </h2>
            {!isGeneratingPDF && (
            <div className="toggle-container !m-0 !gap-2">
            <label className='text-sm'>{t.showTeamImpact}</label>
            <input
                type="checkbox"
                checked={isMbtiToggleOpen}
                onChange={toggleOpen}
                className="theme-checkbox"
                />
            </div>
            )}
            </div>
            <div className="flex-1 border p-4 rounded-md w-full h-fit mt-2 flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <div className="w-full h-[300px] flex items-center justify-center animate-text">
                            <Radar data={mbtiData} options={mbtiOptions} />
                        </div>
                        <div className="text-center mt-4">
                            <h3 className="text-2xl font-bold text-primary">{analysisResults.mbti.type}</h3>
                            <p className="text-gray-600">
                                {isRecruiterMode ? t.candidatesMbtiType : t.yourMbtiType}
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col gap-4 justify-center">
                        <div className="animate-text">
                            <h3 className="mb-1 font-bold">
                                {isRecruiterMode ? t.workplaceApproach : t.workStyle}
                            </h3>
                            <p className="text-sm">{analysisResults.mbti.workStyle}</p>
                        </div>

                        {isRecruiterMode && (
                            <>
                                <div className="animate-text">
                                    <h3 className="mb-1 font-bold">{t.teamDynamics}</h3>
                                    <p className="text-sm">{analysisResults.mbti.teamDynamics}</p>
                                </div>

                                <div className="animate-text">
                                    <h3 className="mb-1 font-bold">{t.communicationStyle}</h3>
                                    <ul className="ml-7 list-disc text-sm">
                                        {analysisResults?.mbti?.communicationPreferences?.map((pref, index) => (
                                            <li key={index}>{pref.charAt(0).toUpperCase() + pref.slice(1)}</li>
                                        ))}
                                    </ul>
                                </div>
                            </>
                            )}

                        {!isRecruiterMode && (
                            <>
                                <div className="animate-text">
                                    <h3 className="mb-1 font-bold">{t.keyStrengths}</h3>
                                    <ul className="ml-7 list-disc text-sm">
                                        {analysisResults?.mbti?.strengths?.map((strength, index) => (
                                            <li key={index}>{strength.charAt(0).toUpperCase() + strength.slice(1)}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="animate-text">
                                    <h3 className="mb-1 font-bold">{t.growthAreas}</h3>
                                    <ul className="ml-7 list-disc text-sm">
                                        {analysisResults?.mbti.challenges?.map((challenge, index) => (
                                            <li key={index}>{challenge.charAt(0).toUpperCase() + challenge.slice(1)}</li>
                                        ))}
                                    </ul>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                
                <div className="border-t pt-4 mt-4">
                    <h3 className="text-lg font-bold mb-4">{t.mbtiDimensions}</h3>
                    <div className="grid grid-cols-2 gap-4">
                        {Object.entries(analysisResults.mbti.dimensions)?.map(([dimension, data], index) => (
                            <div key={dimension} className="rounded-md animate-text flex flex-col">
                                <div className='flex-1'>
                                <h3 className="text-primary font-bold capitalize">
                                    {dimension.replace('_', ' - ')}
                                </h3>
                                <div className="flex justify-between gap-2 items-center mt-2">
                                    <p className="font-semibold text-sm">{t.preference}: {data.preference}</p>
                                    <p className="text-gray-500 text-sm">{data.score}%</p>
                                </div>
                                <div className="relative w-full h-2 bg-gray-200 rounded mt-2">
                                    <div
                                        className="absolute h-full bg-default rounded"
                                        style={{ width: `${data.score}%`, backgroundColor: colors[index] }}
                                    />
                                </div>
                                
                                <p className="text-sm text-gray-600 mt-4">
                                    {isRecruiterMode ? data.workImpact : data.interpretation}
                                </p>
                                </div>

                                {isMbtiToggleOpen && (
                                <div className="text-sm mt-4 font-medium p-2 rounded-md gradient-bg-1">
                                    <p><span className="font-semibold">{t.teamImpact}:</span> {data.teamDynamics}</p>
                                </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                
                {!isRecruiterMode && (
                    <div className="border-t pt-4 mt-4">
                        <h3 className="font-bold text-lg">{t.careerAlignment}</h3>
                        <div className="grid grid-cols-3 gap-4 mt-2 mb-1">
                            <div className='p-4 rounded-md gradient-bg-2'>
                            <span className="text-primary text-sm font-bold mb-1 mt-neg flex items-center gap-2 bg-white py-1.5 px-3 pr-4 rounded-full w-fit mb-4">
                            <img src={logoIcon} className="size-4" />
                                <h3>{t.idealRoles}</h3>
                                </span>
                                <ul className="ml-7 list-disc mt-2">
                                    {analysisResults?.mbti?.careerAlignments?.roleTypes?.map((role, i) => (
                                        <li key={i}>{role.charAt(0).toUpperCase() + role.slice(1)}</li>
                                    ))}
                                </ul>
                            </div>
                            <div className='p-4 rounded-md gradient-bg-2'>
                            <span className="text-primary text-sm font-bold mb-1 mt-neg flex items-center gap-2 bg-white py-1.5 px-3 pr-4 rounded-full w-fit mb-4">
                            <img src={logoIcon} className="size-4" />
                                    <h3>{t.workEnvironmentFit}</h3></span>
                                <ul className="ml-7 list-disc mt-2">
                                    {analysisResults?.mbti?.careerAlignments?.workEnvironments?.map((env, i) => (
                                        <li key={i}>{env.charAt(0).toUpperCase() + env.slice(1)}</li>
                                    ))}
                                </ul>
                            </div>
                            <div className='p-4 rounded-md gradient-bg-2'>
                            <span className="text-primary text-sm font-bold mb-1 mt-neg flex items-center gap-2 bg-white py-1.5 px-3 pr-4 rounded-full w-fit mb-4">
                            <img src={logoIcon} className="size-4" />
                                    <h3>{t.developmentFocus}</h3></span>
                                <ul className="ml-7 list-disc mt-2">
                                    {analysisResults?.mbti?.careerAlignments?.developmentAreas?.map((area, i) => (
                                        <li key={i}>{area.charAt(0).toUpperCase() + area.slice(1)}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MBTISection;